/**
 * src/lib/cvVariantPolling.js — RC 1.52 Phase 3A.2 CV-variant polling helper.
 *
 * Mirror of `coverLetterPolling.js`. The CV-variant generation route
 * returns one of three shapes depending on the kill-switch + dispatch
 * outcome:
 *
 *   • Flag OFF (legacy inline path): sync 200 with the full B1 CV
 *     variant envelope. No polling required.
 *   • Flag ON, fast path (≤25 s LLM): sync 200 with `served_via:
 *     'sync_200'` and the full canonical envelope. No polling.
 *   • Flag ON, replay match: sync 200 with `served_via:
 *     'replay_200'`, `idempotent_replay:true`. No polling.
 *   • Flag ON, slow path: HTTP 202 with `served_via:'async_202'`,
 *     `run_id`, `poll_url`. Frontend polls
 *     `GET /api/cv-variants/{run_id}` with `X-RC-1-52-Poll: 1`
 *     header at the server-suggested cadence (`retry_after_ms`)
 *     until the row reaches a terminal `run_status`
 *     (`complete` | `failed`).
 *
 * CV-specific deltas vs cover-letter helper:
 *   • Polls `/api/cv-variants/{run_id}` (not `/api/cover-letters/`).
 *   • Terminal canonical envelope's `result` block shape mirrors the
 *     legacy B1 generate response: `{ variant_id, variant_name,
 *     role_family, fit_summary, generated_text, created_at,
 *     run_status }`. NO `billing` consumption surface — CV uses
 *     two-phase billing; the caller chains `POST /api/cv-variants/
 *     {variantId}/finalize-charge` AFTER this helper resolves.
 *   • `billing.awaits_finalize=true` is the terminal-complete
 *     marker the section reads to know finalize-charge is the next
 *     step.
 *
 * Operator invariants honoured:
 *   • Invariant P1 — `poll_url` always resolves.
 *   • Invariant P2 — terminal-complete poll surfaces the FULL
 *     canonical envelope under the `X-RC-1-52-Poll: 1` header.
 *   • Invariant P4 — honour `retry_after_ms` when present.
 *   • Invariant R1 — recovery is id-driven (run_id), not title-stamp.
 *   • Invariant R2 — terminal hydration returns the SAME shape as
 *     sync 200 so the CvSection success path is unchanged.
 *   • Invariant R4 — `visibilitychange` triggers an immediate
 *     re-poll if the next scheduled tick is > 2 s away.
 *   • Invariant R5 — backgrounded tab does not lose recovery; the
 *     run_id is the recovery handle (no marker dependency).
 *
 * Hard rails:
 *   • The helper makes ONE LLM-dispatching POST. All subsequent
 *     reads are free polls.
 *   • Idempotency_key is passed through verbatim from the caller —
 *     repeat dispatches under the same key short-circuit to
 *     replay_200 on the backend, never re-reserving.
 *   • Finalize-charge is the SOLE commit path. This helper never
 *     touches /finalize-charge directly; chaining happens in
 *     `CvSection.handleGenerate`.
 */

import { BUNDLE_VERSION } from './buildMeta';
import { playCompletionChime } from './completionChime';

// RC 1.55 Phase Z.4 (operator-locked, 2026-05-13 PM) — dispatch
// surface identifier. Emitted on the `X-Ndor-Dispatch-Surface`
// telemetry header so the backend's ADAPTER_INPUT_ERROR log can
// distinguish workspace dispatches from any future surface that
// adopts the polling helper. Hard-coded per file (NOT parameterised)
// so a future copy-paste of the helper into a new surface trips a
// code-review signal rather than silently inheriting this label.
const _DISPATCH_SURFACE = 'workspace_cv_section_v1';

const DEFAULT_POLL_CADENCE_MS    = 3000;
const POLLING_HARD_CAP_MS        = 120_000;
const MIN_POLL_CADENCE_MS        = 1500;
const VISIBILITY_FAST_POLL_MS    = 500;

/**
 * Polls `GET /api/cv-variants/{run_id}` until terminal state.
 *
 * @param {string} runId
 * @param {object} deps
 * @param {(path:string, opts?:object)=>Promise<object>} deps.apiGet
 * @param {AbortSignal} [deps.signal]
 * @param {(state:string)=>void} [deps.onRunStatus]
 * @returns {Promise<object>}
 */
export async function pollCvVariantRun(runId, deps) {
  const { apiGet, signal, onRunStatus } = deps;
  if (!runId) throw new Error('pollCvVariantRun: runId is required');

  const deadline = Date.now() + POLLING_HARD_CAP_MS;
  let cadence = DEFAULT_POLL_CADENCE_MS;

  let visibilityNudge = null;
  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
      if (visibilityNudge) clearTimeout(visibilityNudge);
      cadence = VISIBILITY_FAST_POLL_MS;
    }
  };
  if (typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', onVisible);
  }

  try {
    while (Date.now() < deadline) {
      if (signal && signal.aborted) {
        throw new Error('Polling aborted');
      }
      let row = null;
      try {
        row = await apiGet(`/api/cv-variants/${runId}`, {
          headers: { 'X-RC-1-52-Poll': '1' },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[RC 1.52 cvVariantPolling] transient read error', err);
        row = null;
      }

      if (row) {
        const status = (row.status || row.run_status || 'complete').toLowerCase();
        if (typeof onRunStatus === 'function') {
          try { onRunStatus(status); } catch { /* noop */ }
        }

        if (status === 'complete') {
          // Long-running completion notification only.
          try { playCompletionChime(); } catch (_) { /* silent */ }
          if (row.result && typeof row.result === 'object') {
            // R2 — flatten to match sync_200 shape so the section
            // reads `r.variant_id` / `r.generated_text` /
            // `r.billing` verbatim.
            return {
              ...row.result,
              billing:           row.billing ?? null,
              served_via:        row.served_via ?? 'poll_200',
              run_id:            row.run_id ?? row.result.variant_id,
              status:            'complete',
              idempotent_replay: row.idempotent_replay ?? false,
            };
          }
          // Direct row passthrough — return the canonical fields
          // the CvSection success path consumes.
          return _rowToCanonical(row);
        }

        if (status === 'failed') {
          const err = new Error(
            row.error_detail || row.run_error_detail
              || 'CV variant generation failed.',
          );
          err.code = row.error_code || row.run_error_code || 'cv_variant_failed';
          err.runStatus = 'failed';
          err.runId = runId;
          throw err;
        }

        if (typeof row.retry_after_ms === 'number'
            && row.retry_after_ms >= MIN_POLL_CADENCE_MS) {
          cadence = row.retry_after_ms;
        }
      }

      // eslint-disable-next-line no-await-in-loop
      await _sleep(cadence, signal);
    }
    // D-3 defensive recoverable error — should NEVER fire after
    // backend D-1 + D-2 (terminal state reached in ≤115 s).
    const err = new Error('CV variant still generating in the background.');
    err.code = 'cv_variant_still_running';
    err.runId = runId;
    err.recoverable = true;
    throw err;
  } finally {
    if (typeof document !== 'undefined' && document.removeEventListener) {
      document.removeEventListener('visibilitychange', onVisible);
    }
    if (visibilityNudge) clearTimeout(visibilityNudge);
  }
}

/**
 * Submit a CV-variant generation request and resolve to the
 * canonical envelope, regardless of whether the backend returned a
 * fast-path result or routed through async polling.
 *
 * @param {object} args
 * @param {object} args.requestBody  POST payload for /api/runs.
 * @param {(path:string, opts:object)=>Promise<object>} args.apiPost
 * @param {(path:string, opts?:object)=>Promise<object>} args.apiGet
 * @param {AbortSignal} [args.signal]
 * @param {(state:string)=>void} [args.onRunStatus]
 * @returns {Promise<object>}
 */
export async function generateCvVariantWithPolling(args) {
  const {
    requestBody, apiPost, apiGet, signal, onRunStatus, telemetryHeaders,
    // CWS-CV (operator-locked, 2026-02-15) — `onRunIdReady` fires
    // once the dispatch's async_202 envelope returns the runId
    // (which is the cv_variants.id). The section uses this to
    // upgrade the inflight marker with `variantId` IMMEDIATELY
    // (before polling resolves), closing the audit-identified
    // pre-runId marker race. Sync_200 / replay_200 paths never
    // invoke this (the marker upgrade still happens post-resolve
    // via the existing `writeInflightCvMarker(..., variantId)`
    // call site in CvSection — see comment at L1445).
    onRunIdReady,
  } = args;
  if (!apiPost || !apiGet) {
    throw new Error('generateCvVariantWithPolling: apiPost + apiGet required');
  }

  // RC 1.55 Phase Z.4 — read-only forensic telemetry. The three
  // headers carry NO PII and NO business data; they exist solely
  // so the backend ADAPTER_INPUT_ERROR log can distinguish which
  // surface / bundle / job_text-resolution-path produced a
  // malformed dispatch. Strict-hold: zero billing / scoring /
  // idempotency / schema impact. The caller (CvSection) supplies
  // `telemetryHeaders.X-Ndor-Job-Text-Source` — this helper does
  // NOT mint or override it.
  const _telemetryHeaders = {
    'X-Ndor-Dispatch-Surface': _DISPATCH_SURFACE,
    'X-Ndor-Bundle-Version':   BUNDLE_VERSION,
    ...(telemetryHeaders || {}),
  };

  const envelope = await apiPost('/api/runs', {
    method:  'POST',
    headers: _telemetryHeaders,
    body:    { action_code: 'cv_variant', payload: requestBody },
  });

  if (!envelope || typeof envelope !== 'object') {
    throw new Error('cv-variant run dispatch returned non-object');
  }

  const served = envelope.served_via || 'sync_200';

  if (served === 'sync_200' || served === 'replay_200') {
    if (envelope.result && typeof envelope.result === 'object') {
      return envelope.result;
    }
    return envelope;
  }

  if (served === 'async_202') {
    const runId = envelope.run_id;
    if (!runId) {
      throw new Error('cv-variant async_202 envelope missing run_id');
    }
    // CWS-CV (operator-locked, 2026-02-15) — surface the runId to
    // the caller BEFORE polling starts. Section uses this to upgrade
    // the inflight CV marker with `variantId` so mount recovery can
    // poll by id (the deterministic B1 path) instead of falling
    // back to the fragile title-stamp branch when the dispatch is
    // interrupted by a tab switch between marker-write and the
    // existing post-resolve marker upgrade.
    if (typeof onRunIdReady === 'function') {
      try { onRunIdReady(runId); } catch { /* noop */ }
    }
    if (typeof onRunStatus === 'function') {
      try { onRunStatus('running'); } catch { /* noop */ }
    }
    return pollCvVariantRun(runId, { apiGet, signal, onRunStatus });
  }

  return envelope.result || envelope;
}

// ─────────────────────────────────────────────────────────────────────
// Internals
// ─────────────────────────────────────────────────────────────────────

function _sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    if (signal && signal.addEventListener) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new Error('Polling aborted'));
      });
    }
  });
}

/**
 * Map a raw `cv_variants` row to the canonical envelope. Used when
 * the polling endpoint returns the back-compat shape (legacy
 * `run_status='complete'` rows pre-dating the Phase 3A.2 polling-
 * envelope branch, OR a no-header request from a non-polling caller).
 */
function _rowToCanonical(row) {
  const cj = row.content_json || {};
  return {
    variant_id:     row.id,
    variant_name:   row.variant_name,
    role_family:    row.role_family,
    fit_summary:    cj,
    generated_text: row.content_markdown || '',
    created_at:     row.created_at,
    run_status:     'complete',
  };
}
